import { formatCurrency } from '@if/core/utils'
import { AreaButton, ControlledChipsField, ControlledPercentField, ErrorHandler, Help, useConfirmDialog } from '@if/mui'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import { Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import {
  PromoPlanDto,
  useGetCurrentPromoPlans,
  useTerminateActiveCustomBuydown,
} from '@src/data/api/merchants-api/merchants-api'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCustomBuydownDialog } from './CustomBuydownDialog'
import { getPromoPlanErrorCode, PromoPlanErrorCode } from './promoPlanErrorCodes'

const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`

const planNotFoundError =
  (plan: PromoPlanDto): ErrorHandler =>
  (_descriptor, t) => ({
    role: 'contextual',
    message: t('merchant.customBuydown.errors.planNotFound', { planCode: plan.promoCodePrefix || plan.label }),
  })

// Minimal form shape this panel needs, shared by the merchant and banner promotion forms.
interface PromoNotificationFormValues {
  promoNotificationEmailList: string[]
  flashPlanRateRebate: number
}

interface Props {
  id: string
  /** Display-only mode, e.g. a child merchant whose parent banner owns the buydown. */
  readOnly?: boolean
  /** Locks only the rate rebate field, e.g. a user without a Buy Down rebate write level. */
  rebateReadOnly?: boolean
}

const StatusPill = ({ status }: { status: PromoPlanDto['status'] }) => {
  const { t } = useTranslation()
  const color = status === 'active' ? 'success' : 'error'
  return <Chip size="small" variant="outlined" color={color} label={t(`merchant.promotionsTab.status.${status}`)} />
}

const FactWithHint = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <Box>
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {hint && <Help title={hint} />}
    </Stack>
    <Typography>{value}</Typography>
  </Box>
)

const BuydownCard = ({
  plan,
  readOnly,
  onEdit,
}: {
  plan: PromoPlanDto
  readOnly: boolean
  onEdit: () => void
}) => {
  const { t } = useTranslation()
  const termRates = [...plan.promoTermRates].sort((a, b) => a.termMonths - b.termMonths)
  const planCode = plan.promoCodePrefix || plan.label

  const [terminateActiveCustomBuydown] = useTerminateActiveCustomBuydown()
  const confirmTerminate = useConfirmDialog<PromoPlanDto>({
    title: (ctx) =>
      t('merchant.customBuydown.terminateConfirmTitle', { planCode: ctx.props.promoCodePrefix || ctx.props.label }),
    message: t('merchant.customBuydown.terminateConfirmMessage'),
    mode: 'destructive',
    confirmLabel: t('merchant.customBuydown.terminateConfirmButton'),
    onError: {
      notFound: planNotFoundError(plan),
      unknown: (descriptor, t) => {
        const code = getPromoPlanErrorCode(descriptor)
        if (code === PromoPlanErrorCode.PlanAlreadyEnded) {
          return { role: 'contextual', message: t('merchant.customBuydown.errors.planAlreadyEnded', { planCode }) }
        }
        return undefined
      },
    },
    onConfirm: (ctx) => terminateActiveCustomBuydown({ merchantId: ctx.props.merchantId, promoPlanId: ctx.props.id }),
  })

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={2.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('merchant.promotionsTab.custom.promotionApplied')}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" mt={0.5}>
              <Typography variant="h6" component="span" fontFamily="monospace">
                {planCode}
              </Typography>
              <StatusPill status={plan.status} />
            </Stack>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2 }}>
            <FactWithHint
              label={t('merchant.customBuydown.contribution')}
              value={formatPercent(plan.merchantFeeRate)}
            />
            <FactWithHint label={t('merchant.customBuydown.adminFee')} value={formatPercent(plan.borrowerFeeRate)} />
            <FactWithHint
              label={t('merchant.customBuydown.maxFinancingAmount')}
              value={
                plan.promoTermRates[0]?.maxFinancingAmount
                  ? formatCurrency(plan.promoTermRates[0].maxFinancingAmount)
                  : t('merchant.customBuydown.noMaximum')
              }
            />
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t('merchant.customBuydown.customerRates')}
              </Typography>
              <Help title={t('merchant.customBuydown.customerRatesTooltip')} />
            </Stack>
            <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 1 }}>
              {termRates.map((rate) => (
                <Box
                  key={rate.termMonths}
                  sx={{ textAlign: 'center', bgcolor: 'action.hover', borderRadius: 1, py: 0.75, px: 1 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {rate.termMonths} {t('common.months')}
                  </Typography>
                  <Typography variant="h4" fontSize="1.4rem" fontWeight={700}>
                    {formatPercent(rate.promoRate)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {!readOnly && (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon fontSize="small" />} onClick={onEdit}>
                {t('merchant.customBuydown.editAction')}
              </Button>
              <Button
                color="error"
                variant="outlined"
                size="small"
                startIcon={<StopCircleOutlinedIcon fontSize="small" />}
                onClick={() => void confirmTerminate.open(plan)}
              >
                {t('merchant.customBuydown.terminateAction')}
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

const CustomPromotionPanel = ({ id, readOnly = false, rebateReadOnly = false }: Props) => {
  const { t } = useTranslation()
  const { control, trigger } = useFormContext<PromoNotificationFormValues>()
  const [currentPlans, isFetching] = useGetCurrentPromoPlans(id)
  const activePlan = currentPlans.find((plan: PromoPlanDto) => plan.status === 'active')
  const dialog = useCustomBuydownDialog(id)

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={t('merchant.customBuydown.notificationsTitle')}
          subheader={t('merchant.customBuydown.notificationsDescription')}
          slotProps={{
            title: { variant: 'subtitle1', color: 'primary' },
            subheader: { variant: 'body2' },
          }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ControlledChipsField
            name="promoNotificationEmailList"
            control={control}
            trigger={trigger}
            label={t('merchant.customBuydown.notificationEmails')}
            placeholder={t('merchant.customBuydown.notificationEmailsPlaceholder')}
            helperText={t('merchant.customBuydown.notificationEmailsHelper')}
            disabled={readOnly}
            format={(v) => v.toLowerCase()}
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title={t('merchant.customBuydown.rebateTitle')}
          subheader={
            <Stack direction="row" alignItems="center" gap={0.5}>
              <span>{t('merchant.customBuydown.rebateDescription')}</span>
              <Help title={t('merchant.customBuydown.rebateTooltip')} />
            </Stack>
          }
          slotProps={{
            title: { variant: 'subtitle1', color: 'primary' },
            subheader: { variant: 'body2' },
          }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ControlledPercentField
            name="flashPlanRateRebate"
            control={control}
            label={t('merchant.customBuydown.rebate')}
            disabled={readOnly || rebateReadOnly}
            fullWidth
            sx={{ maxWidth: 300 }}
          />
        </CardContent>
      </Card>

      {activePlan ? (
        <BuydownCard plan={activePlan} readOnly={readOnly} onEdit={() => void dialog.edit(activePlan)} />
      ) : (
        !readOnly && (
          <AreaButton
            onClick={() => void dialog.create()}
            icon={<AddIcon fontSize="large" />}
            title={t('merchant.customBuydown.emptyTitle')}
            description={t('merchant.customBuydown.emptyDescription')}
          />
        )
      )}
    </>
  )
}

export default CustomPromotionPanel
